import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';
import type { AvailabilitySlot, LearningPreferences, SecondarySubject } from '@/types/registeredStudent';

export const MAX_SUBJECTS_TOTAL = 3; // primary + up to 2 secondary

function authHeaders(accessToken: string): HeadersInit {
  return { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
}

// ─── Availability ────────────────────────────────────────────────────────────

const AVAILABILITY_BASE = '/api/userManagement/v1/me/availability';

export async function getAvailability(accessToken: string): Promise<AvailabilitySlot[]> {
  const res = await apiFetchWithRetry(AVAILABILITY_BASE, {
    method: 'GET',
    headers: authHeaders(accessToken),
  });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) throw new Error(`Failed to fetch availability: ${res.status}`);
  return res.json();
}

export async function replaceAvailability(
  accessToken: string,
  slots: AvailabilitySlot[],
): Promise<AvailabilitySlot[]> {
  const res = await apiFetch(AVAILABILITY_BASE, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ slots }),
  });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) throw new Error(`Failed to save availability: ${res.status}`);
  return res.json();
}

// ─── Learning preferences ────────────────────────────────────────────────────

const PREFS_BASE = '/api/userManagement/v1/me/learning-preferences';

export async function getLearningPreferences(accessToken: string): Promise<LearningPreferences> {
  const res = await apiFetchWithRetry(PREFS_BASE, {
    method: 'GET',
    headers: authHeaders(accessToken),
  });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) throw new Error(`Failed to fetch learning preferences: ${res.status}`);
  const data = await res.json();
  // Normalise: backend returns { learningStyle, languages: string[], pastExperience }
  return {
    learningStyle: data.learningStyle ?? null,
    languages: data.languages ?? [],
    pastExperience: data.pastExperience ?? null,
  };
}

export async function updateLearningPreferences(
  accessToken: string,
  prefs: LearningPreferences,
): Promise<LearningPreferences> {
  const res = await apiFetch(PREFS_BASE, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(prefs),
  });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) throw new Error(`Failed to save learning preferences: ${res.status}`);
  const data = await res.json();
  return {
    learningStyle: data.learningStyle ?? null,
    languages: data.languages ?? [],
    pastExperience: data.pastExperience ?? null,
  };
}

// ─── Secondary subjects ──────────────────────────────────────────────────────

const SECONDARY_BASE = '/api/userManagement/v1/me/secondary-subjects';

export class SecondarySubjectError extends Error {}

export interface AddSecondarySubjectInput {
  subjectName: string;
  subjectId?: string;
  level: string;
  goalType: string;
}

export async function listSecondarySubjects(accessToken: string): Promise<SecondarySubject[]> {
  const res = await apiFetchWithRetry(SECONDARY_BASE, {
    method: 'GET',
    headers: authHeaders(accessToken),
  });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) throw new Error(`Failed to fetch secondary subjects: ${res.status}`);
  return res.json();
}

export async function addSecondarySubject(
  accessToken: string,
  input: AddSecondarySubjectInput,
): Promise<SecondarySubject> {
  const res = await apiFetch(SECONDARY_BASE, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(input),
  });
  if (res.status === 401) handleUnauthorized();
  if (res.status === 409) throw new SecondarySubjectError('You can add up to 3 subjects in total.');
  if (!res.ok) throw new Error(`Failed to add secondary subject: ${res.status}`);
  return res.json();
}

export async function removeSecondarySubject(accessToken: string, id: string): Promise<void> {
  const res = await apiFetch(`${SECONDARY_BASE}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (res.status === 401) handleUnauthorized();
  if (res.status === 404) throw new SecondarySubjectError('Secondary subject not found.');
  if (!res.ok) throw new Error(`Failed to remove secondary subject: ${res.status}`);
}
